import { useState, useMemo, useCallback } from 'react';
import { MaterialData } from '../../../../../../types/materials';

interface MiscFiltersState {
  status: string[];
  productionType: string[];
  partNumber: string[];
  manufacturer: string[];
}

interface UseMiscFiltersProps {
  materials: MaterialData[];
}

export const useMiscFilters = ({ materials }: UseMiscFiltersProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<MiscFiltersState>({
    status: [],
    productionType: [],
    partNumber: [],
    manufacturer: [],
  });

  const uniqueProductionTypes = useMemo(() => {
    const set = new Set<string>();
    materials.forEach((m: MaterialData) => {
      const d = m.details || {};
      const v = d.miscProductionType ?? d.miscProjectStage;
      if (v) set.add(String(v));
    });
    return Array.from(set).sort();
  }, [materials]);

  const uniquePartNumbers = useMemo(() => {
    const set = new Set<string>();
    materials.forEach((m: MaterialData) => {
      const v = m.details?.miscPartNumber;
      if (v) set.add(String(v));
    });
    return Array.from(set).sort();
  }, [materials]);

  const uniqueManufacturers = useMemo(() => {
    const set = new Set<string>();
    materials.forEach((m: MaterialData) => {
      const extra = m.details?.miscExtraDetails as { miscManufacturer?: string } | undefined;
      if (extra?.miscManufacturer) {
        extra.miscManufacturer.split(',').map((s) => s.trim()).filter(Boolean).forEach((x) => set.add(x));
      }
    });
    return Array.from(set).sort();
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    return materials.filter((material: MaterialData) => {
      const searchLower = searchQuery.toLowerCase();
      const d = material.details || {};
      const noteMatch = (d.miscNotes && String(d.miscNotes).toLowerCase().includes(searchLower)) || false;
      const matchesSearch =
        !searchQuery ||
        material.materialId?.toString().includes(searchLower) ||
        (d.miscPartNumber && String(d.miscPartNumber).toLowerCase().includes(searchLower)) ||
        (d.miscHeatNumber && String(d.miscHeatNumber).toLowerCase().includes(searchLower)) ||
        (d.miscLotNumber && String(d.miscLotNumber).toLowerCase().includes(searchLower)) ||
        (d.miscJobNumber && String(d.miscJobNumber).toLowerCase().includes(searchLower)) ||
        (d.miscPoNumber && String(d.miscPoNumber).toLowerCase().includes(searchLower)) ||
        (d.miscSizeOrWeight && String(d.miscSizeOrWeight).toLowerCase().includes(searchLower)) ||
        (d.miscLocation && String(d.miscLocation).toLowerCase().includes(searchLower)) ||
        noteMatch ||
        (material.materialStatus && String(material.materialStatus).toLowerCase().includes(searchLower));

      const matchesStatus = filters.status.length === 0 || filters.status.includes(material.materialStatus || '');

      const pt = d.miscProductionType ?? d.miscProjectStage;
      const ptStr = pt ? String(pt) : '';
      const matchesProd =
        filters.productionType.length === 0 || (ptStr && filters.productionType.includes(ptStr));

      const pn = d.miscPartNumber ? String(d.miscPartNumber) : '';
      const matchesPart = filters.partNumber.length === 0 || (pn && filters.partNumber.includes(pn));

      const manuStr = (material.details?.miscExtraDetails as { miscManufacturer?: string } | undefined)?.miscManufacturer || '';
      const matchesManufacturer =
        filters.manufacturer.length === 0 ||
        filters.manufacturer.some((f) => manuStr.split(',').map((s) => s.trim()).includes(f));

      return matchesSearch && matchesStatus && matchesProd && matchesPart && matchesManufacturer;
    });
  }, [materials, searchQuery, filters]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery !== '' ||
      filters.status.length > 0 ||
      filters.productionType.length > 0 ||
      filters.partNumber.length > 0 ||
      filters.manufacturer.length > 0
    );
  }, [searchQuery, filters]);

  const onFilterChange = useCallback((key: keyof MiscFiltersState) => (value: string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onResetFilters = useCallback(() => {
    setSearchQuery('');
    setFilters({
      status: [],
      productionType: [],
      partNumber: [],
      manufacturer: [],
    });
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueProductionTypes,
    uniquePartNumbers,
    uniqueManufacturers,
    filteredMaterials,
    hasActiveFilters,
    filteredCount: filteredMaterials.length,
    onResetFilters,
  };
};
